import React, { useCallback, useMemo, memo } from 'react';

import formatCurrency, { sumKronor } from './Formatting';
import ArrayEditor from './ArrayEditor';
import CurrencyInput, { isValidCurrency } from './CurrencyInput';
import { normalizeCurrency, isForeign } from './CurrencySelect';
import Alert from '@mui/material/Alert';
import LocationSelect, { LocationValue } from './LocationSelect';
import AllScopesToggle from './AllScopesToggle';
import useIsMobile from './useIsMobile';
import { scopeKey, useScopeKeys, hasScopedPair, useAllScopes } from './Scope';
import { CostCenterItem, DraftRow, ProjectWithExpenseAccounts } from './Types'


export function isValidSupplierInvoiceRows({costCenterItems, rows}: {costCenterItems: CostCenterItem<ProjectWithExpenseAccounts>[], rows: DraftRow[]}): boolean {
    function isValidRow(row: DraftRow): boolean {
        const costCenterItem = getCostCenterItem(costCenterItems, row.cost_center_item_id)
        if (costCenterItem === null) return false
        const project = costCenterItem.projects.find(project => project.project_id === row.project_id)
        return (
            project !== undefined
            && row.expense_account !== null
            && project.expense_account_numbers.includes(row.expense_account)
            && isValidCurrency(row.amount)
        )
    }
    return rows.length > 0 && rows.every(isValidRow)
}

function getCostCenterItem<P>(costCenterItems: CostCenterItem<P>[], costCenterItemId: string | null): CostCenterItem<P> | null {
    return costCenterItems.find(item => item.cost_center_item_id === costCenterItemId) ?? null
}

const SupplierInvoiceRow = memo(({
        labelsEnabled,
        costCenterItems,
        accounts,
        rowId,
        row,
        onChangeRow,
        pickableKeys,
    }: {
        labelsEnabled: boolean,
        costCenterItems: CostCenterItem<ProjectWithExpenseAccounts>[],
        accounts: Record<number, string>,
        rowId: number,
        row: DraftRow,
        onChangeRow: (rowId: number, func: (row: DraftRow) => void) => void,
        pickableKeys: Set<string> | undefined,
    }) => {

    const changeAmountHandler = useCallback(
        (value: number | null) => onChangeRow(rowId, row => {row.amount = value}),
        [onChangeRow, rowId]
    )

    // The three cascading selects are the shared LocationSelect; it clears project + account
    // when the cost-centre changes (and account when the project does), so we just copy the
    // whole picked location onto the row. `?? String(n)` degrades a missing account name to the
    // raw number rather than a blank, so a stale reference is never invisible.
    const changeLocationHandler = useCallback(
        (next: LocationValue) => onChangeRow(rowId, row => {
            row.cost_center_item_id = next.cost_center_item_id
            row.project_id = next.project_id
            row.expense_account = next.account
        }),
        [onChangeRow, rowId]
    )
    const location: LocationValue = {
        cost_center_item_id: row.cost_center_item_id,
        project_id: row.project_id,
        account: row.expense_account,
    }

    return (
        <>
            <LocationSelect
                costCenterItems={costCenterItems}
                accountNumbersOf={project => project.expense_account_numbers}
                accountName={accountNumber => accounts[accountNumber] ?? String(accountNumber)}
                value={location}
                onChange={changeLocationHandler}
                labelsEnabled={labelsEnabled}
                pickableKeys={pickableKeys}
            />
            <CurrencyInput
                disabled={false}
                label={labelsEnabled ? 'Belopp' : undefined}
                value={row.amount}
                onChange={changeAmountHandler}
            />
        </>
    )
})


export const supplierInvoiceRowConstructor: (amount?: number | null) => DraftRow =
    (amount = null) => {
        return {
            cost_center_item_id: null,
            project_id: null,
            expense_account: null,
            amount
        }
    }

function SupplierInvoiceRows({costCenterItems, accounts, rows, onChangeRows, currencyCode}: {
    costCenterItems: CostCenterItem<ProjectWithExpenseAccounts>[],
    accounts: Record<number, string>,
    rows: DraftRow[],
    onChangeRows: (rowsModifier: (rows: DraftRow[]) => void) => void,
    // The currency the amounts are entered in. Shown next to the total so a foreign invoice is
    // never silently read as kronor — the amounts are booked as-is, not converted.
    currencyCode?: string,
}) {
    // On desktop the first row's labels serve as column headers; when rows are stacked (mobile)
    // every row needs its own labels or the later rows are unlabelled selects.
    const isMobile = useIsMobile()
    const scopeKeys = useScopeKeys()
    const { allScopes, toggleAllScopes } = useAllScopes()

    // The pairs a new row may be coded to. Default: the role's scope (undefined ⇒ LocationSelect
    // uses scope). "All scopes" on: any tree pair with a potential attestant. The tree already only
    // holds projects that have an expense account, so an empty result means nothing is bookable.
    const pickableKeys = useMemo(
        () => allScopes
            ? new Set(costCenterItems.flatMap(item =>
                item.projects.filter(project => project.attestant_role_ids.length > 0)
                    .map(project => scopeKey(item.cost_center_item_id, project.project_id))))
            : undefined,
        [allScopes, costCenterItems])
    const hasSelectablePair = hasScopedPair(costCenterItems, pickableKeys ?? scopeKeys)

    const RowComponent = useCallback(({rowId, rowIndex, row, onChangeRow}: {
        rowId: number,
        rowIndex: number,
        row: DraftRow,
        onChangeRow: (rowId: number, func: (row: DraftRow) => void) => void
    }) =>
        <SupplierInvoiceRow
            costCenterItems={costCenterItems}
            accounts={accounts}
            labelsEnabled={rowIndex === 0 || isMobile}
            rowId={rowId}
            row={row}
            onChangeRow={onChangeRow}
            pickableKeys={pickableKeys}
        />,
        [costCenterItems, accounts, isMobile, pickableKeys]
    )

    const total_amount = sumKronor(rows.map(row => row.amount ?? 0))
    // 'kr' for domestic, the ISO code itself for anything else — the codes are what the invoice
    // shows, and inventing symbols for them would be worse than showing 'NOK'.
    const currency = normalizeCurrency(currencyCode)
    const foreign = isForeign(currency)
    const currencyLabel = foreign ? currency : 'kr'

    return <div style={{ alignSelf: 'stretch', display: 'flex', flexFlow: 'column nowrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
        {!hasSelectablePair &&
            <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
                {allScopes
                    ? 'Inga projekt har något utgiftskonto kopplat ännu. Be en administratör koppla ett utgiftskonto (Inställningar → Företag).'
                    : 'Inga av dina kostnadsställen och projekt har något utgiftskonto kopplat, så det finns inget att välja. Be en administratör koppla ett utgiftskonto — eller slå på "Visa alla kostnadsställen" nedan om du fyller i åt någon annan.'}
            </Alert>}
        <ArrayEditor
            gridTemplateColumns='repeat(3, min-content) 7rem'
            rowGap='0.5rem'
            columnGap='1.5rem'
            rows={rows}
            onChangeRows={onChangeRows}
            rowConstructor={supplierInvoiceRowConstructor}
            RowComponent={RowComponent}
        />
        {/* The total is only interesting once the amount is split — but a foreign invoice must show
            its currency next to the money whatever the row count, or a single-row NOK invoice reads
            as kronor. So show the total for several rows, and the unit alone for one foreign row. */}
        {rows.length > 1
            ? <div style={{ alignSelf: 'flex-end' }}>Totalbelopp: {formatCurrency(total_amount)} {currencyLabel}</div>
            : foreign && <div style={{ alignSelf: 'flex-end', fontWeight: 700, color: 'var(--foreign-currency-color, #b26a00)' }}>
                Beloppet är i {currency}</div>}
        {/* Escape hatch, here where a user notices a missing cost-centre. Toggles this tab's own
            persisted "show all scopes" preference (independent of Översikt's). */}
        <AllScopesToggle checked={allScopes} onChange={toggleAllScopes}/>
    </div>
}

export default SupplierInvoiceRows
